import React, {useState, useEffect} from 'react';
import {
  Typography,
  Select,
  MenuItem,
  FormControl,
  IconButton,
  SelectChangeEvent,
} from '@mui/material';
import {Close} from '@mui/icons-material';
import FrontdeskButton from '$components/FrontdeskButton';
import {formatCurrency} from '$modules/frontdesk/utils';
import './styles.scss';
import {useApplyDiscount, useDiscountItemsHook} from '../hooks';
import FetchError from '~/components/FetchError';
import Loading from '~/components/Loading';
import {DEFAULT_DISCOUNT_PERCENTAGE} from '../constant';

interface ApplyDiscountComponentProps {
  applyDiscountModalOpen: boolean;
  setApplyDiscountModalOpen: React.Dispatch<React.SetStateAction<boolean>>;
  currentAmount: string;
  paymentId: string;
}

const ApplyDiscountComponent: React.FC<ApplyDiscountComponentProps> = ({
  setApplyDiscountModalOpen,
  currentAmount,
  paymentId,
}) => {
  const {sendApplyDiscountRequest, isPending: isApplyingDiscount} =
    useApplyDiscount();
  const {discountItemsData, isPending: isDiscountLoading} =
    useDiscountItemsHook();

  const [discountPercentage, setDiscountPercentage] = useState<number>(
    DEFAULT_DISCOUNT_PERCENTAGE
  );
  const [updatedAmount, setUpdatedAmount] = useState<number>(0);
  const [error, setError] = useState<string>('');

  const originalAmount = Number(currentAmount);

  useEffect(() => {
    const discount = (originalAmount * discountPercentage) / 100;
    const newAmount = originalAmount - discount;
    setUpdatedAmount(newAmount);
  }, [discountPercentage, originalAmount]);

  if (isDiscountLoading) {
    return <Loading />;
  }

  if (!discountItemsData) {
    return <FetchError />;
  }

  const handleDiscountChange = (event: SelectChangeEvent<number>) => {
    setDiscountPercentage(Number(event.target.value));
    setError('');
  };

  const handleSaveDiscount = async () => {
    setError('');
    sendApplyDiscountRequest({
      paymentId,
      discountPercentage,
      updatedAmount,
    });

    setApplyDiscountModalOpen(false);
  };

  return (
    <div className="apply-discount-container">
      <div className="apply-discount-header">
        <Typography className="modal-title">Apply discount</Typography>
        <IconButton
          className="close-button"
          onClick={() => setApplyDiscountModalOpen(false)}
        >
          <Close />
        </IconButton>
      </div>

      <div className="apply-discount-content">
        <Typography className="section-title">
          Select the discount percentage
        </Typography>

        <FormControl className="discount-select">
          <Select
            value={discountPercentage}
            onChange={handleDiscountChange}
            displayEmpty
            disabled={isDiscountLoading || isApplyingDiscount}
          >
            {discountItemsData?.discount_percentage &&
              Object.values(discountItemsData.discount_percentage).map(
                option => (
                  <MenuItem key={option} value={option}>
                    {option}%
                  </MenuItem>
                )
              )}
          </Select>
        </FormControl>

        <Typography className="section-title updated-amount-title">
          Updated amount
        </Typography>

        <div className="flex flex-col gap-1 mt-2">
          <div className="flex flex-col gap-1 p-3 bg-white rounded-lg border-container">
            <span className="updated-amount-custom">
              {formatCurrency(updatedAmount)}
            </span>
          </div>
        </div>

        {error && (
          <Typography className="error-message" color="error">
            {error}
          </Typography>
        )}
      </div>

      <div className="apply-discount-footer">
        <FrontdeskButton
          variant="primary"
          title={isApplyingDiscount ? 'Applying...' : 'Save discount'}
          onClick={handleSaveDiscount}
          disabled={isDiscountLoading || isApplyingDiscount}
        />
      </div>
    </div>
  );
};

export default ApplyDiscountComponent;
